import { useEffect, useMemo, useState } from "react";
import { Button, IconButton, Popover, Tooltip } from "@mui/material";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  MESSENGER_IDS,
  MESSENGERS,
  getAccessModeHintForMessengers,
  getGroupShareLink,
  getProjectShareLinks,
  isMessengerId,
  normalizeAvailableMessengers,
} from "../utils/messengers";
import { MaxBrandIcon, TelegramBrandIcon } from "./MessengerBrandIcons";
import { getHrShareInstruction } from "../utils/hrShareGuide";

const MESSENGER_ICONS = {
  telegram: TelegramBrandIcon,
  max: MaxBrandIcon,
};

export function readAvailableMessengersFromSettings(gameSettings) {
  return normalizeAvailableMessengers(gameSettings ?? {});
}

export default function ProjectMessengerAvailability({
  gameId,
  value,
  onChange,
  readOnly = false,
  compact = false,
  showShareLinks = true,
  showShareGuide = false,
  hintExtra,
}) {
  const [copiedId, setCopiedId] = useState(null);
  const [maxBotUsername, setMaxBotUsername] = useState("");
  const [helpAnchor, setHelpAnchor] = useState(null);

  useEffect(() => {
    const baseUrl = (process.env.REACT_APP_SERVER_URL || window.location.origin)
      .replace(/\/$/, "");

    fetch(`${baseUrl}/api/max/bot-info`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data?.username) {
          setMaxBotUsername(String(data.username).replace(/^@/, ""));
        }
      })
      .catch(() => {});
  }, []);

  const availableMessengers = useMemo(() => {
    const normalized = (value ?? []).filter(isMessengerId);
    return normalized.length > 0 ? normalized : ["telegram"];
  }, [value]);

  const shareLinks = useMemo(() => {
    if (!gameId || !showShareLinks) return [];
    const links = getProjectShareLinks(gameId, availableMessengers);
    if (!maxBotUsername) {
      return links;
    }
    return links.map((item) =>
      item.id === "max"
        ? { ...item, link: getGroupShareLink(gameId, "max", maxBotUsername) }
        : item,
    );
  }, [gameId, availableMessengers, showShareLinks, maxBotUsername]);

  const accessHint = getAccessModeHintForMessengers(availableMessengers);
  const hrInstruction = getHrShareInstruction(availableMessengers);
  const helpOpen = Boolean(helpAnchor);

  const handleToggle = (messengerId) => (event) => {
    if (readOnly || !onChange) return;

    const checked = event.target.checked;
    let next = checked
      ? [...availableMessengers, messengerId]
      : availableMessengers.filter((id) => id !== messengerId);

    next = MESSENGER_IDS.filter((id) => next.includes(id));

    if (next.length === 0) {
      return;
    }

    onChange(next);
  };

  const handleCopy = async (text, copyId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(copyId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <div
      className={`app-project-messenger${
        compact ? " app-project-messenger--compact" : ""
      }${readOnly ? " app-project-messenger--readonly" : ""}`}
    >
      <div className="app-project-messenger__header">
        <span className="app-project-messenger__title">
          {readOnly ? "Доступно в мессенджерах" : "Доступность в мессенджерах"}
        </span>
        <IconButton
          size="small"
          className="app-project-messenger__help-btn"
          aria-label="Подсказка по доступу и ссылкам"
          onClick={(event) => setHelpAnchor(event.currentTarget)}
        >
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </div>

      <Popover
        open={helpOpen}
        anchorEl={helpAnchor}
        onClose={() => setHelpAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { className: "app-project-messenger__help-popover" },
        }}
      >
        <div className="app-project-messenger__help-content">
          <p>{accessHint}</p>
          <p>{hrInstruction}</p>
          {hintExtra ? <p>{hintExtra}</p> : null}
        </div>
      </Popover>

      <div className="app-project-messenger__options">
        {MESSENGER_IDS.map((id) => {
          const meta = MESSENGERS[id];
          const BrandIcon = MESSENGER_ICONS[id];
          const checked = availableMessengers.includes(id);
          const isLastSelected = checked && availableMessengers.length === 1;

          return (
            <button
              key={id}
              type="button"
              className={`app-project-messenger__option${
                checked ? " app-project-messenger__option--active" : ""
              }`}
              disabled={readOnly || isLastSelected}
              onClick={() => {
                if (readOnly || isLastSelected) return;
                handleToggle(id)({
                  target: { checked: !checked },
                });
              }}
            >
              <BrandIcon
                className="app-project-messenger__option-icon"
                size={compact ? 18 : 20}
              />
              <span>{meta.label}</span>
            </button>
          );
        })}
      </div>

      {showShareLinks && shareLinks.length > 0 ? (
        <div className="app-project-messenger__links">
          {showShareGuide ? (
            <div className="app-hr-share-guide">
              <strong className="app-hr-share-guide__title">
                Как раздать ссылку HR
              </strong>
              <p className="app-hr-share-guide__text">{hrInstruction}</p>
            </div>
          ) : null}
          {shareLinks.map((item) => (
            <div key={item.id} className="app-project-messenger__link-row">
              <span className="app-project-messenger__link-label">
                {item.label}
              </span>
              <div className="app-project-messenger__link-main">
                <LinkOutlinedIcon className="app-project-messenger__link-icon" />
                <span className="app-project-messenger__link-text">
                  {item.link}
                </span>
              </div>
              <Tooltip
                title={
                  copiedId === item.id ? "Скопировано" : "Копировать ссылку"
                }
                placement="top"
              >
                <Button
                  size="small"
                  variant="contained"
                  className="app-btn app-btn--primary app-btn--compact app-project-messenger__copy-btn"
                  onClick={() => handleCopy(item.link, item.id)}
                >
                  Копировать
                </Button>
              </Tooltip>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
